// ** React imports
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from '@saleshandy/icons';

// ** Internal imports
import { formatNoteTimestamp } from '../../../../utils/date-time';
import { getUserTimeZone } from '../../../../utils/user-details';
import Button from '../../../../design-system/components/atoms/button';
import Modal from '../../../../design-system/components/atoms/modal';
import PostCallOutcomeModalSkeleton from './post-call-outcome-modal-skeleton';
import InlineMentionEditor from '../../../../../components/prospect/components/prospect-kanban/Inline-mention-editor';
import { visibility } from '../../../../../components/dialer/types';
import {
  AttachmentMetaDataType,
  AttachmentType,
  AttachmentUploadStatus,
} from '../../../../editor/types';
import {
  AttachmentDeletePayload,
  AttachmentUploadPayload,
  AttachmentUploadResponse,
} from '../../../../../components/sequence/types';
import { RequestStatus } from '../../../../enums/request-status';
import { ResponseErrorWithHandled } from '../../../../types/response';
import { isContentExceeds2MB } from '../../../../../components/prospect/components/prospect-list/components/prospect-details/prospect-details-modal/tasks-list-tab/utils/helper';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../utils';
import CreateStepModal from '../../../../../components/sequence/shared/modals/create-step-modal';
import {
  SequenceStepType,
  StepType,
} from '../../../../../components/sequence/enums';

import './post-call-outcome-modal.scss';
import { NogenderAvatar } from '../../../../svg/no-gender-avatar';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../../design-system/components/overlay';
import UpgradePlanErrorModal from '../../../upgrade-plan-error-modal';
import hasResource from '../../../../utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../../../utils/access-control/enums/resource-identifiers';
import planPermissionError from '../../../../utils/errors/plan-permission-error';
import { isFreePlan } from '../../../../utils/plans';
import { starterPlanRestriction } from '../../../../utils/plan-restriction';
import { SHModules } from '../../../../enums/module';
import AddProspectManuallyModal from '../../../../../components/prospect/components/prospect-list/components/modals/add-prospect-manually-modal';
import RestrictionErrorModal from '../../../../../components/prospect/components/prospect-list/components/modals/restriction-error-modal';
import {
  getRestrictionPlanModalBodyContent,
  getRestrictionPlanWarningEmailBody,
} from '../../../../../components/prospect/components/prospect-list/utils/helper';
import ImportContactsModal from '../../../../../components/sequence/components/sequence-single-content/components/sequence-single-contacts/components/import-contacts-modal';
import { riskyProspectWarningconstants } from '../../../../../components/sequence/components/sequence-single-content/components/sequence-single-contacts/components/email-verification/risky-prospect-constant';
import LinkNumberToProspectModal from './link-number-to-prospect-modal';
import CallLogsDetailCallOutcomeSelect from '../../../../../components/dialer/components/call-logs/components/call-logs-missed-call-detail/call-logs-detail-call-outcome-select';
import AddToDncModal from '../../../../../components/dialer/components/call-logs/components/add-to-dnc-modal/add-to-dnc-modal';
import { AddPhoneNumberPayload } from '../../../../../components/settings/types/do-not-call-list';
import GlobalRestrictionModal from '../../../../../components/home/components/global-restriction-modal';

// ─── Inline SVG Icons ─────────────────────────────────────────────────────────

const PhoneIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.12 12 19.79 19.79 0 0 1 1.05 3.4 2 2 0 0 1 3 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 5.93 5.93l1.28-1.28a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhoneSlashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_post_call_dnc)">
      <path
        d="M2 14L14 2"
        stroke="#6B7280"
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.88733 9.44069C2.76964 7.83531 2.11656 5.95268 2 4.00002C2 3.6464 2.14048 3.30726 2.39052 3.05721C2.64057 2.80716 2.97971 2.66669 3.33333 2.66669H6L7.33333 6.00002L5.66667 7.00002C5.73867 7.14669 5.81533 7.29002 5.89667 7.43002M7.064 8.94802C7.626 9.50802 8.27867 9.97735 9 10.3334L10 8.66669L13.3333 10V12.6667C13.3333 13.0203 13.1929 13.3594 12.9428 13.6095C12.6928 13.8595 12.3536 14 12 14C9.39874 13.8443 6.94502 12.7397 5.104 10.8954"
        stroke="#6B7280"
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_post_call_dnc">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const InfoCircleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="12"
      y1="8"
      x2="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="12"
      y1="16"
      x2="12.01"
      y2="16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CircleCheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="22 4 12 14.01 9 11.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <polyline
      points="6 9 12 15 18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

// ─── Types ────────────────────────────────────────────────────────────────────

type OutcomeOption = {
  id: number;
  name: string;
  isDefault: boolean;
  sentiment: string;
};

export type PostCallOutcomeModalProps = {
  show: boolean;
  isLoading: boolean;
  /** Dialer call log id — used as InlineMentionEditor attachment scope */
  callId: number;
  callerName: string | null;
  callerPhone: string;
  isIncomingCall: boolean;
  callViaLabel: string | null;
  callViaNumber: string;
  wasRecording: boolean;
  completedAt: string | null;
  callDuration: number | null;
  outcomeList: OutcomeOption[];
  /** Note typed during the call — pre-populates the note editor on open */
  initialNote?: string;
  onDone: (data: {
    outcomeId: number | null;
    noteContent: string;
    noteVisibility: visibility;
    attachmentIds: number[];
  }) => void;
  handleSendAttachmentUploadRequest: (payload: AttachmentUploadPayload) => void;
  handleResetUploadAttachmentRequest: () => void;
  handleSendDeleteAttachmentRequest: (payload: AttachmentDeletePayload) => void;
  handleResetDeleteAttachmentRequest: () => void;
  uploadAttachmentRequestStatus: RequestStatus;
  uploadAttachmentRequestError?: ResponseErrorWithHandled;
  uploadedAttachment?: AttachmentUploadResponse;
  deleteAttachmentRequestStatus: RequestStatus;
  deleteAttachmentRequestError?: ResponseErrorWithHandled;
  deletedAttachmentId?: number;
  /** Prospect on the call — required to create a follow-up task */
  prospectId: number | null;
  sendCreateTaskRequest: (payload: unknown) => void;
  createTaskRequestStatus: RequestStatus;
  resetCreateTaskRequest: () => void;
  /** Quota string from home meta — same source as prospect header */
  remainingProspectLimit?: string;
  addPhoneNumberRequestStatus: RequestStatus;
  sendAddPhoneNumberRequest: (payload: AddPhoneNumberPayload) => void;
  resetAddPhoneNumberRequest: () => void;
  getCallLogsProspectProfileSectionResponse?: any;
  isDemoAccount: boolean;
  sendGetCallOutcomeListRequest?: () => void;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PostCallOutcomeModal: React.FC<PostCallOutcomeModalProps> = ({
  show,
  isLoading,
  callId,
  callerName,
  callerPhone,
  isIncomingCall,
  callViaLabel,
  callViaNumber,
  wasRecording = true,
  completedAt,
  callDuration,
  outcomeList,
  onDone,
  handleSendAttachmentUploadRequest,
  handleResetUploadAttachmentRequest,
  handleSendDeleteAttachmentRequest,
  handleResetDeleteAttachmentRequest,
  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadedAttachment,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  prospectId,
  sendCreateTaskRequest,
  createTaskRequestStatus,
  resetCreateTaskRequest,
  remainingProspectLimit,
  addPhoneNumberRequestStatus,
  sendAddPhoneNumberRequest,
  resetAddPhoneNumberRequest,
  initialNote = '',
  getCallLogsProspectProfileSectionResponse,
  isDemoAccount,
  sendGetCallOutcomeListRequest,
}) => {
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<number | null>(
    null,
  );
  const [
    isCreateTaskStepModalVisible,
    setIsCreateTaskStepModalVisible,
  ] = useState(false);
  const [stepType, setStepType] = useState(StepType.Call);
  const [initialStepType, setInitialStepType] = useState(SequenceStepType.Call);
  const [noteContent, setNoteContent] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<visibility>(
    visibility.Public,
  );
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const [attachmentsMeta, setAttachmentsMeta] = useState<
    AttachmentMetaDataType[]
  >([]);
  const [showError, setShowError] = useState('');
  const [
    showAddProspectManuallyModal,
    setShowAddProspectManuallyModal,
  ] = useState(false);
  const [showImportContactsModal, setShowImportContactsModal] = useState(false);
  const [showUpgradePlanErrorModal, setShowUpgradePlanErrorModal] = useState(
    false,
  );
  const [isRestrictionModalVisible, setIsRestrictionModalVisible] = useState(
    false,
  );
  const [showLinkNumberMenu, setShowLinkNumberMenu] = useState(false);
  const [
    showLinkNumberToProspectModal,
    setShowLinkNumberToProspectModal,
  ] = useState(false);
  const [showDncModal, setShowDncModal] = useState(false);
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  const planUpgradeError = useCallback(() => {
    const title = 'Please upgrade your plan to start adding prospects';
    const content = [
      'Your account is downgraded to a free plan. Upgrade to paid plan to add prospects and resume email sending.',
    ];
    planPermissionError.showModal(title, content, true);
  }, []);

  const showRestrictionErrorModal = useCallback(
    () => setIsRestrictionModalVisible(true),
    [],
  );

  const onAddProspectsButtonClick = useCallback(() => {
    if (isFreePlan()) {
      planUpgradeError();
      return;
    }
    if (starterPlanRestriction()) {
      setShowUpgradePlanErrorModal(true);
      return;
    }
    if (parseInt(String(remainingProspectLimit ?? '0'), 10) === 0) {
      showRestrictionErrorModal();
      return;
    }
    if (hasResource(ResourceIdentifiers.CONTACTS_IMPORT)) {
      setShowAddProspectManuallyModal(true);
    }
  }, [planUpgradeError, remainingProspectLimit, showRestrictionErrorModal]);

  useEffect(() => {
    if (!show) return;
    setSelectedOutcomeId(null);
    setNoteContent(initialNote ?? '');
    setNoteVisibility(visibility.Public);
    setAttachments([]);
    setAttachmentsMeta([]);
    setShowError('');
    setIsCreateTaskStepModalVisible(false);
    setShowAddProspectManuallyModal(false);
    setShowImportContactsModal(false);
    setShowUpgradePlanErrorModal(false);
    setIsRestrictionModalVisible(false);
    setShowLinkNumberMenu(false);
    setShowLinkNumberToProspectModal(false);
    setShowDncModal(false);
    sendGetCallOutcomeListRequest?.();
  }, [show, callId, initialNote, sendGetCallOutcomeListRequest]);

  const onStepTypeChange = useCallback((label: StepType) => {
    switch (label) {
      case StepType.WriteWithAi:
        setStepType(StepType.WriteWithAi);
        setInitialStepType(SequenceStepType.WriteWithAi);
        break;
      case StepType.LinkedIn:
        setStepType(StepType.LinkedIn);
        setInitialStepType(SequenceStepType.LinkedInConnectionRequest);
        break;
      case StepType.Call:
        setStepType(StepType.Call);
        setInitialStepType(SequenceStepType.Call);
        break;
      case StepType.WhatsApp:
        setStepType(StepType.WhatsApp);
        setInitialStepType(SequenceStepType.WhatsAppMessage);
        break;
      case StepType.Task:
        setStepType(StepType.Task);
        setInitialStepType(SequenceStepType.Custom);
        break;
      default:
        setStepType(StepType.LinkedIn);
        setInitialStepType(SequenceStepType.LinkedInConnectionRequest);
    }
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: createTaskRequestStatus,
      onSuccess: () => {
        setIsCreateTaskStepModalVisible(false);
        resetCreateTaskRequest();
      },
      onFailed: () => {
        resetCreateTaskRequest();
      },
    });
  }, [createTaskRequestStatus, resetCreateTaskRequest]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addPhoneNumberRequestStatus,
      onSuccess: () => {
        setShowDncModal(false);
        resetAddPhoneNumberRequest();
      },
      onFailed: () => {
        resetAddPhoneNumberRequest();
      },
    });
  }, [addPhoneNumberRequestStatus, resetAddPhoneNumberRequest]);

  const handleAttachmentsChange = useCallback(
    (updatedAttachments: AttachmentType[]): void => {
      setAttachments(updatedAttachments);
    },
    [],
  );

  const handleAttachmentStatusChange = useCallback(
    (updatedAttachment: AttachmentType) => {
      setAttachments((prevAttachments) =>
        prevAttachments.map((attachment) =>
          (attachment.attachmentIdentifier &&
            updatedAttachment.attachmentIdentifier &&
            attachment.attachmentIdentifier ===
              updatedAttachment.attachmentIdentifier) ||
          (attachment.attachmentId != null &&
            attachment.attachmentId === updatedAttachment.attachmentId)
            ? {
                ...attachment,
                ...updatedAttachment,
                status:
                  updatedAttachment.status ||
                  AttachmentUploadStatus.UploadSuccess,
              }
            : attachment,
        ),
      );
    },
    [],
  );

  const buildAttachmentIds = useCallback((): number[] => {
    const ids =
      attachments
        ?.map((attachment) => attachment?.attachmentId)
        .filter((id): id is number => id !== undefined && id !== null) ?? [];
    return ids;
  }, [attachments]);

  const handleDone = useCallback(() => {
    if (isContentExceeds2MB(noteContent || '')) {
      setShowError('Content size cannot exceed 2MB');
      return;
    }
    attachments.forEach((attachment) => {
      if (attachment.status === AttachmentUploadStatus.Uploading) {
        const attachmentMeta = attachmentsMeta?.find(
          (item) =>
            item?.attachmentIdentifier === attachment?.attachmentIdentifier,
        );
        if (attachmentMeta?.source) {
          attachmentMeta.source.cancel('Upload has been cancelled.');
        }
      }
    });
    setAttachmentsMeta([]);
    setShowError('');
    onDone({
      outcomeId: selectedOutcomeId,
      noteContent,
      noteVisibility,
      attachmentIds: buildAttachmentIds(),
    });
  }, [
    attachments,
    attachmentsMeta,
    buildAttachmentIds,
    noteContent,
    noteVisibility,
    onDone,
    selectedOutcomeId,
  ]);

  const isAttachmentRequestPending =
    getIsRequestPending(uploadAttachmentRequestStatus) ||
    getIsRequestPending(deleteAttachmentRequestStatus);

  const callOutcomeSelectRow = useMemo(
    () => ({
      id: callId,
      callOutcome:
        selectedOutcomeId != null
          ? (() => {
              const found = outcomeList.find((x) => x.id === selectedOutcomeId);
              return {
                id: selectedOutcomeId,
                name: String(found?.name ?? ''),
                isDefault: Boolean(found?.isDefault),
                sentiment: String(found?.sentiment ?? ''),
              };
            })()
          : null,
    }),
    [callId, selectedOutcomeId, outcomeList],
  );

  const formattedDate = formatNoteTimestamp(
    completedAt ?? '',
    false,
    getUserTimeZone(),
    true,
  );

  const headerContent = (
    <div className="bs-post-call-outcome-modal__header">
      <div className="bs-post-call-outcome-modal__icon-tag">
        <PhoneIcon />
      </div>
      <div className="bs-post-call-outcome-modal__header-text">
        <span className="semibold-3 gray-txt-9">Call Completed</span>
        <div className="bs-post-call-outcome-modal__header-meta">
          {formattedDate && (
            <span className="regular-1 gray-txt-11">{formattedDate}</span>
          )}
          {callDuration !== null && (
            <span className="bs-post-call-outcome-modal__duration-pill">
              {formatDuration(callDuration)}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        show={true}
        titleContent={headerContent}
        showCloseIcon={false}
        hideFooter
        backdrop="static"
        className="bs-post-call-outcome-modal"
        onHide={() =>
          onDone({
            outcomeId: null,
            noteContent: '',
            noteVisibility,
            attachmentIds: [],
          })
        }
      >
        {/* Body */}
        <div className="bs-post-call-outcome-modal__body">
          {isLoading ? (
            <PostCallOutcomeModalSkeleton />
          ) : (
            <>
              {/* Prospect Row */}
              <div className="bs-post-call-outcome-modal__prospect-row">
                <div className="bs-post-call-outcome-modal__avatar">
                  <NogenderAvatar width={64} height={64} />
                </div>
                <div className="bs-post-call-outcome-modal__prospect-info">
                  {callerName ? (
                    <div className="prospect-name">
                      <span className="semibold-3 gray-txt-9">
                        {callerName}
                      </span>
                      <span className="regular-1 gray-txt-11">
                        {callerPhone}
                      </span>
                    </div>
                  ) : (
                    <span className="semibold-3 gray-txt-9">{callerPhone}</span>
                  )}
                </div>
                <OverlayTooltip text="Do Not Call">
                  <div
                    className="bs-post-call-outcome-modal__dnc-btn"
                    {...accessibleOnClick(() => {
                      if (isDemoAccount) {
                        setShowGlobalRestrictionModal(true);
                        return;
                      }
                      setShowDncModal(true);
                    })}
                  >
                    <PhoneSlashIcon />
                  </div>
                </OverlayTooltip>
              </div>

              {/* Call Via */}
              <div className="bs-post-call-outcome-modal__call-via">
                <span className="modal-label semibold-1 gray-txt-11">
                  {isIncomingCall ? 'Call Received On' : 'Call Via'}
                </span>
                <span className="regular-2 gray-txt-9">
                  {callViaLabel
                    ? `${callViaLabel} (${callViaNumber})`
                    : callViaNumber}
                </span>
              </div>

              {/* Call Outcome */}
              <div className="bs-post-call-outcome-modal__field">
                <label className="modal-label semibold-1 gray-txt-11">
                  Call Outcome
                </label>
                {/* <SimpleOptionsDropdown
                  className="call-outcome-option-dropdown"
                  options={outcomeOptions}
                  selectedKey={selectedOutcomeId}
                  placeholder="Select an outcome"
                  // searchPlaceholder="Search" //TODO : Enable Search
                  // showSearch
                  renderOptionIcon={renderOptionIcon}
                  onChange={(option) => {
                    setSelectedOutcomeId(Number(option.key));
                  }}
                /> */}
                <CallLogsDetailCallOutcomeSelect
                  row={callOutcomeSelectRow}
                  onChange={(option) =>
                    setSelectedOutcomeId(Number(option.key))
                  }
                />
              </div>

              {/* Note */}
              <div className="bs-post-call-outcome-modal__field">
                <label className="modal-label semibold-1 gray-txt-11">
                  Note
                </label>
                <InlineMentionEditor
                  key={`post-call-outcome-note-editor-${callId}`}
                  className={`bs-post-call-outcome-modal-note-editor ${
                    attachments.length > 0 ? 'has-attachments' : ''
                  }`}
                  value={noteContent}
                  onEditorChange={setNoteContent}
                  showAddAttachments
                  showToolBarContainer
                  showFooterToolbarContainer
                  showFooterVisibilitySelector
                  showFooterSaveButton={false}
                  showFooterCancelButton={false}
                  selectedVisibilityKey={noteVisibility}
                  onVisibilityChange={(key) =>
                    setNoteVisibility(key as visibility)
                  }
                  placeholder={
                    noteVisibility === visibility.Private
                      ? 'Visible only to you and people tagged here'
                      : 'Visible to everyone in the organisation*'
                  }
                  attachments={attachments}
                  onAttachmentsChange={handleAttachmentsChange}
                  onAttachmentStatusChange={handleAttachmentStatusChange}
                  sendAttachmentUploadRequest={
                    handleSendAttachmentUploadRequest
                  }
                  uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
                  uploadAttachmentRequestError={uploadAttachmentRequestError}
                  uploadedAttachment={uploadedAttachment}
                  resetUploadAttachmentRequest={
                    handleResetUploadAttachmentRequest
                  }
                  sendDeleteAttachmentRequest={
                    handleSendDeleteAttachmentRequest
                  }
                  deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
                  deleteAttachmentRequestError={deleteAttachmentRequestError}
                  deletedAttachmentId={deletedAttachmentId}
                  resetDeleteAttachmentRequest={
                    handleResetDeleteAttachmentRequest
                  }
                  onAttachmentsMetaChange={(meta) => {
                    setAttachmentsMeta(meta || []);
                  }}
                  attachmentMsg
                  uniqueId={callId}
                  attachmentsClassname="notes-list-tab-attachment"
                  notesTabAttachment
                  error={showError?.length > 0}
                  errorMessage={showError}
                />
              </div>

              {/* Recording Banner */}
              {wasRecording && (
                <div className="bs-post-call-outcome-modal__recording-banner">
                  <InfoCircleIcon />
                  <div className="bs-post-call-outcome-modal__recording-banner-content">
                    <span className="semibold-1 gray-txt-9">
                      Recording &amp; Transcription
                    </span>
                    <span className="regular-1 gray-txt-9">
                      Recording and Transcription of your call is being
                      processed. Once done you can access it from call log.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Divider */}
          <div className="bs-post-call-outcome-modal__divider" />
        </div>

        {/* Footer */}
        <div className="bs-post-call-outcome-modal__footer">
          {!prospectId ? (
            <div
              className={`bs-post-call-outcome-modal__split-btn ${
                showLinkNumberMenu ? 'show-link-number-menu' : ''
              }`}
            >
              <OverlayTooltip
                text="Create a new prospect using this number."
                placement={Placement.Top}
              >
                <Button
                  variant={Button.Variant.Outlined}
                  onClick={onAddProspectsButtonClick}
                >
                  Add as Prospect
                </Button>
              </OverlayTooltip>
              <OverlayPopover
                show={showLinkNumberMenu}
                onToggle={setShowLinkNumberMenu}
                placement={Placement.TopEnd}
                trigger={TriggerType.Click}
                rootClose
                className="bs-post-call-outcome-modal__link-number-popover p-0"
                content={
                  <div className="bs-post-call-outcome-modal__link-number-popover-inner">
                    <button
                      type="button"
                      className="bs-post-call-outcome-modal__link-number-row bs-post-call-outcome-modal__link-number-row--highlight regular-2 gray-txt-9"
                      {...accessibleOnClick(() => {
                        setShowLinkNumberMenu(false);
                        setShowLinkNumberToProspectModal(true);
                      })}
                    >
                      Link number to existing prospect
                    </button>
                  </div>
                }
              >
                <Button
                  variant={Button.Variant.Outlined}
                  className="bs-post-call-outcome-modal__split-chevron"
                  aria-expanded={showLinkNumberMenu}
                  aria-haspopup="true"
                  aria-label="More add prospect options"
                >
                  <ChevronDownIcon />
                </Button>
              </OverlayPopover>
            </div>
          ) : (
            <div>
              <Button
                variant={Button.Variant.Outlined}
                disabled={
                  !prospectId ||
                  getIsRequestPending(createTaskRequestStatus) ||
                  isLoading
                }
                {...accessibleOnClick(() => {
                  if (prospectId) {
                    setStepType(StepType.Call);
                    setInitialStepType(SequenceStepType.Call);
                    setIsCreateTaskStepModalVisible(true);
                  }
                })}
                className="bs-post-call-outcome-modal__add-followup-task-btn"
              >
                <Plus width={16} height={16} />
                Add Follow-up Task
              </Button>
            </div>
          )}
          <Button
            variant={Button.Variant.Primary}
            theme={Button.Theme.Solid}
            onClick={handleDone}
            disabled={isAttachmentRequestPending}
            iconLeft={<CircleCheckIcon />}
          >
            Done
          </Button>
        </div>

        {prospectId && isCreateTaskStepModalVisible ? (
          <div {...accessibleOnClick((e) => e.stopPropagation())}>
            <CreateStepModal
              show={isCreateTaskStepModalVisible}
              stepType={stepType}
              initialStepType={initialStepType}
              onClose={() => setIsCreateTaskStepModalVisible(false)}
              subsequenceId={undefined}
              onStepTypeChange={onStepTypeChange}
              excludeStepTypes={[StepType.Email]}
              isTaskModal
              sendCreateTaskRequest={sendCreateTaskRequest}
              prospectId={prospectId}
              className="create-task-modal"
              showCloseIcon={false}
              CallLogId={getCallLogsProspectProfileSectionResponse?.id}
              taskAssigneeFromDialer={true}
            />
          </div>
        ) : null}
      </Modal>

      {showAddProspectManuallyModal && (
        <AddProspectManuallyModal
          show={showAddProspectManuallyModal}
          onClose={() => setShowAddProspectManuallyModal(false)}
          callId={callId}
          callerPhone={callerPhone}
        />
      )}
      {showImportContactsModal ? (
        <ImportContactsModal
          warning={riskyProspectWarningconstants.PROSPECT_DEFAULT_WARNING}
          show={showImportContactsModal}
          onClose={() => setShowImportContactsModal(false)}
          fromModule={SHModules.Prospect}
        />
      ) : null}
      {showUpgradePlanErrorModal ? (
        <UpgradePlanErrorModal
          show={showUpgradePlanErrorModal}
          onClose={() => setShowUpgradePlanErrorModal(false)}
          planName="starter"
          restrictionOn="prospect"
        />
      ) : null}
      {isRestrictionModalVisible ? (
        <RestrictionErrorModal
          show={isRestrictionModalVisible}
          onClose={() => setIsRestrictionModalVisible(false)}
          bodyContent={getRestrictionPlanModalBodyContent()}
          emailBody={getRestrictionPlanWarningEmailBody()}
        />
      ) : null}
      {showLinkNumberToProspectModal ? (
        <LinkNumberToProspectModal
          show={showLinkNumberToProspectModal}
          onClose={() => setShowLinkNumberToProspectModal(false)}
          callerPhone={callerPhone}
          callId={callId}
          onSubmit={() => {}}
        />
      ) : null}
      {showDncModal && (
        <AddToDncModal
          show={showDncModal}
          onClose={() => setShowDncModal(false)}
          phoneNumber={callerPhone}
          isSubmitting={addPhoneNumberRequestStatus === RequestStatus.Pending}
          onAddToDnc={(dncListId) => {
            sendAddPhoneNumberRequest({
              dncListId,
              phoneNumber: callerPhone,
              updateOutcome: false,
            });
          }}
        />
      )}

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Adding to DNC list is disabled in demo mode"
        content="This is a demo account with sample data. Signup to add to DNC list"
      />
    </>
  );
};

export default memo(PostCallOutcomeModal);
